import type { SpaceTimeConfig } from './config';
import { ConnectionManager, type ConnectionManagerOptions } from './core/ConnectionManager';
import { ReducerClient } from './core/ReducerClient';
import { SubscriptionFactory } from './core/SubscriptionFactory';
import { AuthModule } from './modules/AuthModule';
import { PlayerModule } from './modules/PlayerModule';
import { MapModule } from './modules/MapModule';
import { IntentModule } from './modules/IntentModule';
import { InventoryModule } from './modules/InventoryModule';
import { SocialModule } from './modules/SocialModule';
import { GameTickModule } from './modules/GameTickModule';
import { AdminModule } from './modules/AdminModule';
import { DungeonModule } from './modules/DungeonModule';
import { MapEventModule } from './modules/MapEventModule';
import { SkillModule } from './modules/SkillModule';
import { CatalogModule } from './modules/CatalogModule';
import type { Logger } from './types';

// Re-export types for backward compatibility
export type {
  IntentSubmission,
  IntentQueueQuery,
  IntentQueueRecord,
  TickCommitInput,
  RegisterPlayerPayload,
  LoginPayload,
  PlayerAppearanceUpdatePayload,
  PlayerPositionUpdatePayload,
  PlayerSnapshot,
  AdminStatusSnapshot,
  RuntimeEntitySnapshot,
  InventorySlotRecord,
  ItemBarSlotRecord,
  CatalogSnapshot,
  ItemCatalogEntry,
  SkillCatalogEntry,
  AppearanceCatalogEntry,
  PlayerRosterSubscription,
  PlayerSubscriptionOptions,
  GameTickSubscription,
  GameTickSubscriptionOptions,
  SpawnPoint,
  MapPayload,
  AuthoritativeMapDefinition,
  AdminMapSummary,
  MapPortalRecord,
  PlacedNpcRecord
} from './types';

export interface SpaceTimeClientOptions {
  readonly config: SpaceTimeConfig;
  readonly moduleName?: string;
  readonly builderFactory?: ConnectionManagerOptions['builderFactory'];
  readonly logger?: Logger;
}

/**
 * SpaceTimeClient - Coordinator for SpacetimeDB operations.
 *
 * Exposes domain-specific functionality through modules:
 * - `auth` - Registration, login, logout
 * - `players` - Player data and subscriptions
 * - `maps` - Map definitions, portals, NPCs
 * - `intents` - Intent queue operations
 * - `inventory` - Inventory and item bar
 * - `social` - Chat, friends, guilds
 * - `gameTick` - Game tick subscriptions
 * - `admin` - Admin-only operations
 * - `dungeon` - Dungeon entry
 */
export class SpaceTimeClient {
  // Core infrastructure (internal)
  private readonly connectionManager: ConnectionManager;
  private readonly reducerClient: ReducerClient;
  private readonly subscriptionFactory: SubscriptionFactory;

  // Domain modules (public)
  public readonly auth: AuthModule;
  public readonly players: PlayerModule;
  public readonly maps: MapModule;
  public readonly intents: IntentModule;
  public readonly inventory: InventoryModule;
  public readonly social: SocialModule;
  public readonly gameTick: GameTickModule;
  public readonly admin: AdminModule;
  public readonly dungeon: DungeonModule;
  public readonly mapEvents: MapEventModule;
  public readonly skills: SkillModule;
  public readonly catalogs: CatalogModule;

  constructor(options: SpaceTimeClientOptions) {
    const logger = options.logger ?? (() => undefined);

    // Initialize core infrastructure
    this.connectionManager = new ConnectionManager({
      config: options.config,
      moduleName: options.moduleName,
      builderFactory: options.builderFactory,
      logger
    });
    this.reducerClient = new ReducerClient(this.connectionManager, logger);
    this.subscriptionFactory = new SubscriptionFactory(this.connectionManager, logger);

    // Initialize domain modules
    this.auth = new AuthModule(this.connectionManager, this.reducerClient, logger);
    this.players = new PlayerModule(this.connectionManager, this.reducerClient, this.subscriptionFactory, logger);
    this.maps = new MapModule(this.connectionManager, this.reducerClient, this.subscriptionFactory, logger);
    this.intents = new IntentModule(this.connectionManager, this.reducerClient, logger);
    this.inventory = new InventoryModule(this.connectionManager, this.reducerClient, logger);
    this.social = new SocialModule(this.connectionManager, this.reducerClient, this.subscriptionFactory, logger);
    this.gameTick = new GameTickModule(this.connectionManager, logger);
    this.admin = new AdminModule(this.connectionManager, this.reducerClient, this.subscriptionFactory, logger);
    this.dungeon = new DungeonModule(this.connectionManager, this.reducerClient, logger);
    this.mapEvents = new MapEventModule(this.connectionManager, logger);
    this.skills = new SkillModule(this.connectionManager, this.reducerClient, logger);
    this.catalogs = new CatalogModule(this.connectionManager, this.reducerClient, this.subscriptionFactory, logger);
  }

  // --- Connection methods (convenience accessors) ---

  public async connect(): Promise<void> {
    await this.connectionManager.connect();
  }

  public async disconnect(options?: { skipLogout?: boolean }): Promise<void> {
    await this.connectionManager.disconnect(options);
  }

  public isConnected(): boolean {
    return this.connectionManager.isConnected();
  }

  public getPlayerId(): string | null {
    return this.connectionManager.getPlayerId();
  }

  public getConfig(): SpaceTimeConfig {
    return this.connectionManager.getConfig();
  }

  /** Returns the active DbConnection, connecting if needed. */
  public async ensureConnection(): Promise<ReturnType<ConnectionManager['ensureConnection']>> {
    return this.connectionManager.ensureConnection();
  }
}
